import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, PieChart, TrendingUp, Users, Target,
  Calendar, Download, Filter, Settings,
  Eye, ExternalLink, Plus, RefreshCw, Truck, Building,
  AlertTriangle, Activity, Clock, ChevronRight, ArrowLeft,
  IndianRupee
} from 'lucide-react';
import SalesDashboard from './SalesDashboard';
// import QuoteOrderDashboard from './QuoteOrderDashboard';
// import ShipmentAnalytics from './ShipmentAnalytics';
import PartyFinancials from './PartyFinancials';
import TargetsDashboard from './TargetsDashboard';
import { formatIndianAmount } from '../../../utils/amountFormatter';

const CRMAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [userRole] = useState('Super Admin'); // This would come from auth context
  const [targetBuilderActive, setTargetBuilderActive] = useState(false); // FIXED: Added state for target builder

  const analyticsModules = [
    {
      id: 'sales',
      title: 'Sales & CRM Dashboards',
      description: 'Pipeline, forecast, team performance, and conversion analytics',
      icon: BarChart3,
      color: 'bg-blue-500',
      metrics: [
        { label: 'Pipeline Value', value: `₹${formatIndianAmount(45200000)}`, change: '+12%' },
        { label: 'Win Rate', value: '23.8%', change: '+2.1%' },
        { label: 'Avg Deal Size', value: `₹${formatIndianAmount(2800000)}`, change: '+8%' }
      ]
    },
    /* {
      id: 'quotes',
      title: 'Quote → Order Tracking',
      description: 'Proposal traction and conversion to orders',
      icon: PieChart,
      color: 'bg-green-500',
      metrics: [
        { label: 'Quote→Order %', value: '68.5%', change: '+5.2%' },
        { label: 'Avg Discount', value: '12.3%', change: '-1.1%' },
        { label: 'Time to Accept', value: '8.2 days', change: '-0.8d' }
      ]
    }, */
    {
      id: 'targets',
      title: 'Target vs Achievement',
      description: 'Sales targets, performance tracking, and team analytics',
      icon: Target,
      color: 'bg-purple-500',
      metrics: [
        { label: 'Target Achievement', value: '87.5%', change: '+5.2%' },
        { label: 'Revenue Gap', value: `₹${formatIndianAmount(2400000)}`, change: '-0.8M' },
        { label: 'Team Performance', value: '78.3%', change: '-1.5%' }
      ]
    },
    {
      id: 'logistics',
      title: 'Courier Analytics',
      description: 'Detailed courier performance and delivery analytics powered by Shipway',
      icon: Truck,
      color: 'bg-orange-500',
      metrics: [
        { label: 'Avg Rate/kg', value: `₹${formatIndianAmount(45.2)}`, change: '-2.1%' },
        { label: 'Success Rate', value: '94.2%', change: '+1.8%' }
      ]
    },
    {
      id: 'financials',
      title: 'Party Financials (Marg ERP)',
      description: 'Outstanding, collections, and aging analysis',
      icon: Building,
      color: 'bg-red-500',
      metrics: [
        { label: 'Total Outstanding', value: `₹${formatIndianAmount(45800000)}`, change: '+8.5%' },
        { label: '% Overdue', value: '23.8%', change: '+1.5%' },
        { label: 'Avg Collection', value: '42.5 days', change: '-2.1d' }
      ]
    }
  ];

  // FIXED: Enhanced Target button handlers - New Target now switches to builder tab
  const handleNewTarget = (e) => {
    // Prevent any default button behavior and page navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Switch to targets tab if not already there
    if (activeTab !== 'targets') {
      setActiveTab('targets');
    }

    // FIXED: Set target builder to active to switch to builder tab
    setTargetBuilderActive(true);

    showNotification('Opening Target Builder...', 'info');
  };

  const handleCreateTarget = (e, targetData) => {
    // Prevent any default button behavior and page navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // This would handle the actual target creation with form data
    const newTarget = {
      id: 'TGT-' + Date.now(),
      type: targetData?.type || 'Sales Target',
      assignedTo: targetData?.assignedTo || 'Current User',
      amount: targetData?.amount || `₹${formatIndianAmount(1000000)}`,
      deadline: targetData?.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      status: 'Active',
      progress: 0
    };

    showNotification(`Target "${newTarget.type}" created successfully for ${newTarget.assignedTo}!`, 'success');
  };

  // FIXED: Added Save as Draft functionality
  const handleSaveAsDraft = (e, targetData) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Save current form data as draft
    const draftTarget = {
      id: 'DRAFT-' + Date.now(),
      type: targetData?.type || 'Sales Target',
      assignedTo: targetData?.assignedTo || 'Current User',
      amount: targetData?.amount || '',
      deadline: targetData?.deadline || '',
      status: 'Draft',
      savedAt: new Date().toLocaleString(),
      progress: 0
    };

    // In real app, this would save to localStorage or database
    localStorage.setItem('targetDraft', JSON.stringify(draftTarget));

    showNotification('Target saved as draft successfully!', 'success');
  };

  // FIXED: Added Import from CSV functionality
  const handleImportFromCSV = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Create hidden file input for CSV upload
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';

    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          try {
            const csvData = e.target.result;
            const lines = csvData.split('\n');
            const headers = lines[0].split(',').map(h => h.trim());

            // Expected CSV format: Target Type, Assigned To, Target Amount, Deadline
            const targets = [];
            for (let i = 1; i < lines.length; i++) {
              if (lines[i].trim()) {
                const values = lines[i].split(',').map(v => v.trim());
                if (values.length >= 4) {
                  targets.push({
                    id: 'CSV-' + Date.now() + '-' + i,
                    type: values[0] || 'Sales Target',
                    assignedTo: values[1] || 'Current User',
                    amount: values[2] || '₹0',
                    deadline: values[3] || new Date().toLocaleDateString(),
                    status: 'Imported',
                    progress: 0
                  });
                }
              }
            }

            if (targets.length > 0) {
              showNotification(`Successfully imported ${targets.length} targets from CSV!`, 'success');

              // In real app, you would send these to API or state management
              // For demo, save to localStorage
              localStorage.setItem('importedTargets', JSON.stringify(targets));
            } else {
              showNotification('No valid target data found in CSV file', 'warning');
            }
          } catch (error) {
            showNotification('Error parsing CSV file. Please check format.', 'error');
          }
        };

        fileReader.readAsText(file);
      }
    };

    // Trigger file selection
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);

    showNotification('Select CSV file to import targets...', 'info');
  };

  const handleExportTargets = (e) => {
    // Prevent any default button behavior and page navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const targetData = [
      ['Target Type', 'Assigned To', 'Target Amount', 'Achievement', 'Status'],
      ['Sales Target', 'John Doe', `₹${formatIndianAmount(5000000)}`, '87.5%', 'In Progress'],
      ['Revenue Target', 'Jane Smith', `₹${formatIndianAmount(3000000)}`, '92.3%', 'On Track'],
      ['Team Target', 'Marketing Team', `₹${formatIndianAmount(2500000)}`, '76.8%', 'Behind']
    ];

    const csvContent = targetData.map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `targets-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showNotification('Targets exported successfully!', 'success');
  };

  // FIXED: Enhanced schedule functionality for targets with preventDefault
  const handleScheduleTargetReports = (e) => {
    // Prevent any default button behavior and page navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const scheduleOptions = {
      reportType: 'Target Achievement Reports',
      frequency: 'weekly',
      nextDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      recipients: [process.env.REACT_APP_COMPANY_OFFICIAL_MAIL],
      includeMetrics: ['Target Achievement %', 'Revenue Gap', 'Team Performance'],
      alertThresholds: {
        belowTarget: '< 80%',
        criticalGap: '> ₹5M'
      }
    };

    showNotification(
      `Target reports scheduled for ${scheduleOptions.frequency} delivery! Next: ${scheduleOptions.nextDelivery}`,
      'success',
      4000
    );

  };

  // FIXED: Enhanced create schedule functionality with preventDefault
  const handleCreateSchedule = (e) => {
    // Prevent any default button behavior and page navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const scheduleOptions = {
      reportType: 'Custom Analytics Schedule',
      frequency: 'weekly',
      nextDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      recipients: [process.env.REACT_APP_COMPANY_OFFICIAL_MAIL],
      modules: analyticsModules.map(m => m.title),
      customFilters: {
        dateRange: 'Last 30 days',
        includeCharts: true,
        format: 'PDF + Excel'
      }
    };

    showNotification(
      `Custom analytics schedule created! Next delivery: ${scheduleOptions.nextDelivery}`,
      'success',
      4000
    );

  };

  // FIXED: Enhanced Quick Actions with actual functionality
  const handleExportReports = () => {
    const currentDate = new Date().toISOString().split('T')[0];

    // Create comprehensive analytics export data
    const exportData = [
      ['Module', 'Metric', 'Value', 'Change'],
      ['Overview', 'Total Revenue', `₹${formatIndianAmount(2400000)}`, '+12%'],
      ['Overview', 'Active Opportunities', '156', '+8'],
      ['Overview', 'Team Performance', '87%', 'Above target'],
      ...analyticsModules.flatMap(module =>
        module.metrics.map(metric => [
          module.title,
          metric.label,
          metric.value,
          metric.change
        ])
      )
    ];

    // Convert to CSV
    const csvContent = exportData.map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `crm-analytics-report-${currentDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success notification
    showNotification('Analytics report exported successfully!', 'success');
  };

  const handleScheduleReports = () => {
    const scheduleOptions = {
      reportTypes: ['All Analytics', 'Sales Dashboard', 'Target Achievement', 'Logistics KPIs', 'Party Financials'],
      frequency: 'weekly',
      nextDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      recipients: [process.env.REACT_APP_COMPANY_OFFICIAL_MAIL]
    };

    // Show scheduling confirmation
    showNotification(
      `Report scheduled successfully! Next delivery: ${scheduleOptions.nextDelivery} (${scheduleOptions.frequency})`,
      'success',
      4000
    );

  };

  const handleAnalyticsSettings = () => {
    const currentSettings = {
      dataRefreshInterval: '5 minutes',
      autoExportEnabled: true,
      alertThresholds: {
        winRate: '< 20%',
        pipelineValue: '< ₹10M',
        teamPerformance: '< 70%'
      },
      dashboardLayout: 'standard',
      timezone: 'IST'
    };

    showNotification('Opening Analytics Settings...', 'info');

    // In real app, this would open a settings modal

    setTimeout(() => {
      showNotification('Settings updated successfully!', 'success');
    }, 1500);
  };

  const handleAlertsThresholds = () => {
    const currentAlerts = [
      { metric: 'Win Rate', threshold: '< 20%', status: 'active', lastTriggered: '2 hours ago' },
      { metric: 'Pipeline Value', threshold: '< ₹10M', status: 'inactive', lastTriggered: 'Never' },
      { metric: 'Team Performance', threshold: '< 70%', status: 'active', lastTriggered: '1 day ago' },
      { metric: 'Quote Conversion', threshold: '< 50%', status: 'active', lastTriggered: '3 hours ago' }
    ];

    showNotification('Loading alerts and thresholds configuration...', 'info');

    // In real app, this would open alerts configuration modal

    setTimeout(() => {
      showNotification(`${currentAlerts.filter(a => a.status === 'active').length} alerts are currently active`, 'warning');
    }, 1000);
  };

  // Utility function for notifications
  const showNotification = (message, type = 'info', duration = 3000) => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500'
    };

    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-sm`;
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-1">
          <div class="text-sm font-medium">${message}</div>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-white hover:text-gray-200">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(notification);

    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 300);
      }
    }, duration);
  };

  const quickActions = [
    { icon: Download, label: 'Export Reports', action: handleExportReports },
    { icon: Calendar, label: 'Schedule Reports', action: handleScheduleReports },
    { icon: Settings, label: 'Analytics Settings', action: handleAnalyticsSettings },
    { icon: AlertTriangle, label: 'Alerts & Thresholds', action: handleAlertsThresholds }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{formatIndianAmount(2400000)}</p>
              <p className="text-sm text-green-600">+12% from last month</p>
            </div>
            <IndianRupee className="h-8 w-8 text-green-500 flex items-center justify-center font-bold text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Opportunities</p>
              <p className="text-2xl font-bold text-gray-900">156</p>
              <p className="text-sm text-[#0F2942]">+8 this week</p>
            </div>
            <Activity className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Team Performance</p>
              <p className="text-2xl font-bold text-gray-900">87%</p>
              <p className="text-sm text-green-600">Above target</p>
            </div>
            <Users className="h-8 w-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Data Freshness</p>
              <p className="text-2xl font-bold text-gray-900">2m</p>
              <p className="text-sm text-gray-600">Last updated</p>
            </div>
            <Clock className="h-8 w-8 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Analytics Modules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {analyticsModules.map((module) => {
          const IconComponent = module.icon;
          return (
            <div key={module.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setActiveTab(module.id)}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`${module.color} p-2 rounded-lg`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{module.title}</h3>
                    <p className="text-sm text-gray-600">{module.description}</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(-1);
                  }}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  <ArrowLeft className="w-3 h-3" />
                  <span>Back</span>
                </button>
              </div>
              <div className="space-y-2">
                {module.metrics.map((metric, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{metric.label}</span>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">{metric.value}</span>
                      <span className={`ml-2 text-xs ${metric.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
                        }`}>
                        {metric.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const IconComponent = action.icon;
            return (
              <button key={index}
                onClick={action.action}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 group">
                <IconComponent className="h-6 w-6 text-gray-600 mb-2 group-hover:text-gray-800 transition-colors" />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'sales':
        return <SalesDashboard />;
      /* case 'quotes':
        return <QuoteOrderDashboard />; */
      case 'targets':
        return <TargetsDashboard
          onExport={handleExportTargets}
        />;
      // case 'logistics':
      //   return <ShipmentAnalytics />;
      case 'financials':
        return <PartyFinancials />;
      default:
        return renderOverview();
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics & Reporting</h1>
            <p className="text-gray-600">Comprehensive CRM insights, dashboards, and scheduled reports</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Role: {userRole}</span>
            <div className="h-2 w-2 bg-green-500 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview'
              ? 'border-blue-500 text-[#0F2942]'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            Overview
          </button>
          {analyticsModules.map((module) => (
            <button
              key={module.id}
              onClick={() => {
                setActiveTab(module.id);
                if (module.id !== 'targets') {
                  setTargetBuilderActive(false); // Reset target builder when switching away
                }
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === module.id
                ? 'border-blue-500 text-[#0F2942]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {module.title.split(' ')[0]}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

export default CRMAnalytics;
